import { Component } from '@angular/core'
import {
  IBankingConfiguration,
  IReadyForPayment,
  PaymentType,
  ProviderODataService
} from '@exchange/domain/.'
import { Observable, Subscriber } from 'rxjs'
import { tap } from 'rxjs/operators'
import moment from 'moment'
import { UtilService } from '@exchange/services/utilService'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'

export interface IPaymentDetailsModel {
  paymentType: string
  paymentDate: Date
  totalPayment: number
  memo: string
  payerAccountNumberQualifier: string
  payerName: string
  payerRoutingNumber: string
  payerAccountNumber: string
  payeeAccountNumberQualifier: string
  payeeName: string
  payeeNPI: string
  payeeRoutingNumber: string
  payeeAccountNumber: string
  readyForPayments: IReadyForPayment[]
}

@Component({
  selector: 'app-payment-details',
  template: '',
  standalone: false
})
export class PaymentDetailsComponent {
  observable: Observable<IPaymentDetailsModel> = null
  model: IPaymentDetailsModel = this.getDefaultPaymentModel()
  PaymentType = PaymentType

  private observer: Subscriber<IPaymentDetailsModel> = null

  constructor(
    private providerService: ProviderODataService,
    private modalService: BsModalService
  ) {}

  private getDefaultPaymentModel(): IPaymentDetailsModel {
    return {
      paymentType: PaymentType.EFT,
      paymentDate: new Date(),
      totalPayment: 0,
      memo: null,
      payerAccountNumberQualifier: null,
      payerName: null,
      payerRoutingNumber: null,
      payerAccountNumber: null,
      payeeAccountNumberQualifier: null,
      payeeName: null,
      payeeNPI: '',
      payeeRoutingNumber: null,
      payeeAccountNumber: null,
      readyForPayments: []
    }
  }

  private hydratePaymentWithProviderBankingConfiguration(
    bankingConfig: IBankingConfiguration
  ): IPaymentDetailsModel {
    return Object.assign(this.model, {
      payerAccountNumberQualifier: bankingConfig.BPR08PayerAccountNumberQualifier,
      payerName: bankingConfig.BPR10PayerIdentifier,
      payerRoutingNumber: bankingConfig.BPR07PayerRoutingNumber,
      payerAccountNumber: bankingConfig.BPR09PayerAccountNumber,
      payeeAccountNumberQualifier: bankingConfig.BPR14PayeeAccountNumberQualifier,
      payeeRoutingNumber: bankingConfig.BPR13PayeeRoutingNumber,
      payeeAccountNumber: bankingConfig.BPR15PayeeAccountNumber
    })
  }

  public collectPaymentDetails(readyForPayments: IReadyForPayment[], payAmount: number) {
    this.model = this.getDefaultPaymentModel()

    if (payAmount === 0) {
      this.model.paymentType = PaymentType.INFO
    }

    this.model.payeeNPI = readyForPayments[0].Npi
    this.model.payeeName = readyForPayments[0].Name
    this.model.totalPayment = payAmount
    this.model.readyForPayments = readyForPayments

    return this.providerService
      .query()
      .select('BankingConfiguration')
      .filter(`NPI eq '${this.model.payeeNPI}'`)
      .exec()
      .pipe(
        tap(result => {
          if (result.data.length) {
            const bankingConfiguration = result.data[0].BankingConfiguration
            if (bankingConfiguration) {
              this.hydratePaymentWithProviderBankingConfiguration(bankingConfiguration)
            }
          }

          this.observable = new Observable<IPaymentDetailsModel>(observer => {
            this.observer = observer
            this.modalService.show(PaymentDetailsModalComponent, {
              initialState: {
                model: this.model,
                cancel: this.cancel,
                ok: this.ok
              },
              class: 'modal-lg'
            })
          })
        })
      )
  }

  ok = (model: IPaymentDetailsModel) => {
    this.model = model
    this.observer.next(this.model)
    this.observer.complete()
    this.observer = null
    this.observable = null
  }

  cancel = () => {
    this.observer.next(null)
    this.observer.complete()
    this.observer = null
    this.observable = null
  }
}

@Component({
  templateUrl: './payment-details.component.html',
  styleUrls: ['./payment-details.component.scss'],
  standalone: false
})
export class PaymentDetailsModalComponent {
  cancel: () => void
  ok: (model: IPaymentDetailsModel) => void
  model: IPaymentDetailsModel

  PaymentType = PaymentType
  constructor(
    public modalRef: BsModalRef,
    public util: UtilService
  ) {}
  okClick() {
    this.modalRef.hide()
    this.ok(this.model)
  }
  cancelClick() {
    this.modalRef.hide()
    this.cancel()
  }

  getModelErrors(): any[] {
    const errors = []

    if (this.model.paymentType === this.PaymentType.INFO) {
      if (this.model.totalPayment !== 0) {
        errors.push('Amount must be 0 to use INFO type.')
      }

      if (!this.model.memo) {
        errors.push('Memo is required.')
      }
    }

    if (this.model.paymentType === this.PaymentType.CHK) {
      if (this.model.totalPayment <= 0) {
        errors.push('Cannot make a negative or 0 payment.')
      }

      if (!this.model.memo) {
        errors.push('Memo is required.')
      }

      const today = moment().startOf('day')
      if (moment(this.model.paymentDate).isBefore(today)) {
        errors.push('Date must be in the future.')
      }
    }

    if (this.model.paymentType === this.PaymentType.EFT) {
      if (this.model.totalPayment <= 0) {
        errors.push('Cannot make a negative or 0 payment.')
      }

      if (!this.model.memo) {
        errors.push('Memo is required.')
      }
      const today = moment().startOf('day')
      if (moment(this.model.paymentDate).isBefore(today)) {
        errors.push('Date must be in the future.')
      }
      if (!this.model.payerName) {
        errors.push('CHI Identifier is required.')
      }
      if (!this.model.payerAccountNumber) {
        errors.push('CHI Account # is required.')
      }
      if (!this.model.payerRoutingNumber) {
        errors.push('CHI ABA Routing # is required.')
      }
      if (!this.model.payeeAccountNumber) {
        errors.push('Receiver Account Number is required.')
      }
      if (!this.model.payeeRoutingNumber) {
        errors.push('Receiver Routing # is required.')
      }
    }
    return errors
  }

  modelStateIsValid() {
    const errors = this.getModelErrors()
    return errors.length === 0
  }
}
